import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    ArrowForward as ArrowForwardIcon,
    Groups as GroupsIcon,
    Payments as PaymentsIcon,
    ReceiptLong as ReceiptIcon,
    Search as SearchIcon,
    Security as SecurityIcon,
    Settings as SettingsIcon,
    SupportAgent as SupportIcon,
    VolunteerActivism as DonateIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'

const BRAND_ORANGE = '#FF8A00'
const cardSx = {
    borderRadius: 6,
    background: 'rgba(16, 11, 38, 0.92)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
}

const HelpCenter = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { lang } = useLanguage()
    const [query, setQuery] = useState('')

    const isMerchant = user?.role === 'merchant'
    const isDiaspora = ['US', 'CA', 'UK', 'GB', 'AU', 'FR', 'EU'].includes(
        String(user?.country || '').toUpperCase()
    )

    const topics = useMemo(
        () => [
            {
                title: isMerchant
                    ? (lang === 'fr' ? 'Reglements et preuves' : 'Settlements and proof')
                    : (lang === 'fr' ? 'Transactions et recus' : 'Transactions and receipts'),
                desc: isMerchant
                    ? (lang === 'fr'
                        ? 'Ouvrez les reglements, suivez les paiements recus, et confirmez ce qui a ete encaisse.'
                        : 'Open settlements, track received payments, and confirm what has been collected.')
                    : (lang === 'fr'
                        ? 'Ouvrez les preuves, suivez les paiements manquants, et confirmez ce qui a ete regle.'
                        : 'Open proof, track missing payments, and confirm what was settled.'),
                icon: <ReceiptIcon />,
                to: '/transactions',
                tags: isMerchant ? ['settlement', 'proof', 'invoice'] : ['proof', 'receipt', 'history'],
            },
            {
                title: isMerchant
                    ? (lang === 'fr' ? 'Creer une facture' : 'Create an invoice')
                    : (lang === 'fr' ? 'Envoyer de l argent' : 'Send money'),
                desc: isMerchant
                    ? (lang === 'fr'
                        ? 'Creation de facture, lien de paiement, date d echeance et parcours client.'
                        : 'Invoice creation, payment links, due dates, and the customer payment flow.')
                    : (lang === 'fr'
                        ? 'Taux, frais, livraison et guide du numero du beneficiaire pour les transferts au pays.'
                        : 'Rates, fees, delivery, and receiver-number guidance for transfers back home.'),
                icon: <PaymentsIcon />,
                to: isMerchant ? '/invoices/create' : '/send',
                tags: isMerchant ? ['invoice', 'payment link', 'due date'] : ['send', 'fees', 'delivery'],
            },
            {
                title: lang === 'fr' ? 'Payer un commerce' : 'Pay a business',
                desc: lang === 'fr'
                    ? 'Recherchez des commerces, entrez d abord les montants locaux, et verifiez clairement le change.'
                    : 'Search businesses, enter local amounts first, and review the FX clearly.',
                icon: <PaymentsIcon />,
                to: '/payment',
                tags: ['business', 'payment', 'fx'],
            },
            {
                title: lang === 'fr' ? 'Dons' : 'Donations',
                desc: lang === 'fr'
                    ? 'Recus, preuve de soutien, et questions sur les dons transfrontaliers.'
                    : 'Receipts, proof of support, and cross-border giving questions.',
                icon: <DonateIcon />,
                to: '/donations',
                tags: ['donation', 'receipt', 'support'],
            },
            {
                title: lang === 'fr' ? 'Osusu et contributions' : 'Osusu and contributions',
                desc: lang === 'fr'
                    ? 'Epargne de groupe, preuve de contribution, et suivi des echeances.'
                    : 'Group savings, contribution proof, and due-date follow-up.',
                icon: <GroupsIcon />,
                to: '/contributions',
                tags: ['osusu', 'group', 'savings'],
            },
            {
                title: lang === 'fr' ? 'Profil et acces au compte' : 'Profile and account access',
                desc: lang === 'fr'
                    ? 'Mettez a jour votre identite, votre profil de compte, et vos informations d acces.'
                    : 'Update identity details, account profile, and access information.',
                icon: <SecurityIcon />,
                to: '/profile',
                tags: ['profile', 'account', 'access'],
            },
            {
                title: lang === 'fr' ? 'Parametres et preferences' : 'Settings and preferences',
                desc: lang === 'fr'
                    ? 'Notifications, mises a jour du profil, et preferences de langue.'
                    : 'Notifications, profile updates, and language preferences.',
                icon: <SettingsIcon />,
                to: '/settings',
                tags: ['settings', 'notifications', 'profile'],
            },
        ],
        [lang, isMerchant]
    )

    const filteredTopics = useMemo(() => {
        const needle = query.trim().toLowerCase()
        if (!needle) return topics

        return topics.filter((topic) =>
            `${topic.title} ${topic.desc} ${topic.tags.join(' ')}`.toLowerCase().includes(needle)
        )
    }, [query, topics])

    const quickGuides = [
        {
            title: isDiaspora
                ? (lang === 'fr' ? 'Guide diaspora pour payer' : 'Diaspora payer guide')
                : (lang === 'fr' ? 'Guide local pour payer' : 'Local payer guide'),
            body: isDiaspora
                ? (lang === 'fr'
                    ? 'Entrez d abord le montant local pour les factures et paiements de commerces, puis MojoPayr affiche votre debit dans la devise de residence avec frais.'
                    : 'Enter the local amount first for bills and business payments, then MojoPayr shows your resident-currency charge with fees.')
                : (lang === 'fr'
                    ? 'Les comptes locaux restent dans leur marche de residence pour des paiements plus simples, plus legers, et plus adaptes au mobile money.'
                    : 'Local accounts stay in their resident market so payments stay simple, lighter, and more mobile-money friendly.'),
        },
        {
            title: isMerchant
                ? (lang === 'fr' ? 'Support entreprise' : 'Business support')
                : (lang === 'fr' ? 'Preuves et recus' : 'Proof and receipts'),
            body: isMerchant
                ? (lang === 'fr'
                    ? 'Utilisez le centre d aide pour les encaissements, suivis de factures, et preuves de transactions des marchands.'
                    : 'Use Help Center to explain collections, invoice follow-up, and transaction proof for merchants.')
                : (lang === 'fr'
                    ? 'Utilisez Transactions pour rouvrir les preuves, references de paiement, et recus de service au meme endroit.'
                    : 'Use Transactions to reopen proof, payment references, and service receipts in one place.'),
        },
    ]

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="lg">
                <Card
                    sx={{
                        ...cardSx,
                        mb: 3,
                        overflow: 'hidden',
                        background:
                            'linear-gradient(135deg, rgba(16,11,38,0.96) 0%, rgba(44,22,102,0.94) 62%, rgba(255,138,0,0.16) 100%)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={1.4}>
                            <Chip icon={<SupportIcon sx={{ color: `${BRAND_ORANGE} !important` }} />} label={lang === 'fr' ? 'Centre d aide MojoPayr' : 'MojoPayr Help Center'} sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,138,0,0.12)', color: BRAND_ORANGE, fontWeight: 900, border: '1px solid rgba(255,138,0,0.24)' }} />
                            <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: { xs: 30, md: 42 } }}>
                                {isMerchant
                                    ? (lang === 'fr' ? 'Centre d aide entreprise' : 'Business Help Center')
                                    : (lang === 'fr' ? 'Centre d aide' : 'Help Center')}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.80)', maxWidth: 840 }}>
                                {isMerchant
                                    ? (lang === 'fr'
                                        ? 'Accedez plus vite a l aide pour les factures, les reglements, les clients et les preuves de paiement.'
                                        : 'Get faster support for invoices, settlements, customers, and payment proof.')
                                    : (lang === 'fr'
                                        ? 'Des reponses claires, un routage plus rapide, et moins de bruit. Trouvez vite le bon parcours MojoPayr, que vous envoyiez depuis la diaspora, payiez en local, ou geriez une entreprise.'
                                        : 'Clean answers, faster routing, and less noise. Find the right MojoPayr flow quickly, whether you are sending from the diaspora, paying locally, or running a business.')}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={8}>
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                <TextField fullWidth value={query} onChange={(event) => setQuery(event.target.value)} placeholder={lang === 'fr' ? 'Rechercher recus, transferts, taxes, factures, aide compte...' : 'Search receipts, transfers, tax, invoices, account help...'} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: 'rgba(255,255,255,0.55)' }} /></InputAdornment>) }} sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { color: '#fff', borderRadius: 999, background: 'rgba(255,255,255,0.05)', '& fieldset': { borderColor: 'rgba(255,255,255,0.10)' } } }} />

                                <Grid container spacing={2}>
                                    {filteredTopics.map((topic) => (
                                        <Grid item xs={12} md={6} key={topic.title}>
                                            <Card sx={{ borderRadius: 5, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
                                                <CardContent sx={{ p: 2.4 }}>
                                                    <Stack direction="row" spacing={1.4} alignItems="flex-start">
                                                        <Box sx={{ width: 46, height: 46, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,138,0,0.12)', color: BRAND_ORANGE, flexShrink: 0 }}>{topic.icon}</Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography sx={{ color: '#fff', fontWeight: 900 }}>{topic.title}</Typography>
                                                            <Typography sx={{ color: 'rgba(255,255,255,0.66)', mt: 0.7 }}>{topic.desc}</Typography>
                                                            <Stack direction="row" spacing={0.8} flexWrap="wrap" sx={{ mt: 1.3, gap: 0.8 }}>
                                                                {topic.tags.map((tag) => (
                                                                    <Chip key={tag} label={tag} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.76)' }} />
                                                                ))}
                                                            </Stack>
                                                            <Button variant="text" endIcon={<ArrowForwardIcon />} onClick={() => navigate(topic.to)} sx={{ mt: 1.5, px: 0, textTransform: 'none', fontWeight: 900, color: BRAND_ORANGE }}>
                                                                {lang === 'fr' ? 'Ouvrir le sujet' : 'Open topic'}
                                                            </Button>
                                                        </Box>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                        <Stack spacing={3}>
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 1.5 }}>{lang === 'fr' ? 'Guide rapide' : 'Quick guidance'}</Typography>
                                    <Stack spacing={1.2}>
                                        {quickGuides.map((guide) => (
                                            <Box key={guide.title} sx={{ p: 1.6, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                                <Typography sx={{ color: '#fff', fontWeight: 800, mb: 0.6 }}>{guide.title}</Typography>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.66)', fontSize: 13.5 }}>{guide.body}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Card sx={cardSx}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 1.5 }}>{lang === 'fr' ? 'Liens rapides' : 'Fast links'}</Typography>
                                    <Stack spacing={1.2}>
                                        <Button variant="contained" fullWidth onClick={() => navigate('/transactions')} sx={{ borderRadius: 999, py: 1.2, textTransform: 'none', fontWeight: 900, bgcolor: BRAND_ORANGE }}>{lang === 'fr' ? 'Ouvrir Transactions' : 'Open Transactions'}</Button>
                                        <Button variant="outlined" fullWidth onClick={() => navigate('/faq')} sx={{ borderRadius: 999, py: 1.2, textTransform: 'none', fontWeight: 800, color: '#fff', borderColor: 'rgba(255,255,255,0.14)' }}>{lang === 'fr' ? 'Lire les FAQ' : 'Read FAQs'}</Button>
                                        <Button variant="text" fullWidth onClick={() => navigate('/contact')} sx={{ borderRadius: 999, py: 1.1, textTransform: 'none', fontWeight: 800, color: 'rgba(255,255,255,0.82)' }}>{lang === 'fr' ? 'Contacter le support' : 'Contact support'}</Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )
}

export default HelpCenter


